import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ApiError, authApi } from "../shared/api.ts";
import { useAuth } from "../shared/auth-context.tsx";
import { checkComposition, checkLength } from "../shared/password-rules.ts";
import { AuthLayout, AuthStatusMark } from "../components/AuthLayout.tsx";

/**
 * /invite/accept?token=... — the team-invite landing page.
 *
 * The invitee chooses a password here; submitting POSTs {token, password} to
 * /api/auth/accept-invite, which checks the password BEFORE redeeming the
 * single-use token. So a weak password (400) keeps the form up with the
 * token intact, while a token problem (invalid/expired) ends in the
 * "Go to sign in" state. On success the session hint is set and we land on
 * /overview, same as the magic-link callback.
 *
 * Nothing is sent on mount: an invite is redeemed only when the invitee
 * has a password to go with it. A missing token never calls the API.
 */
export function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<"form" | "submitting" | "success" | "error">(
    token ? "form" : "error",
  );
  const [error, setError] = useState<string | null>(
    token ? null : "This invite link is missing its token.",
  );
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  // A password the server refused: shown under the form, form stays.
  const [formError, setFormError] = useState<string | null>(null);

  const lengthOk = checkLength(pw);
  const compositionOk = checkComposition(pw);
  const matches = confirm.length > 0 && confirm === pw;
  const canSubmit = lengthOk && compositionOk && matches && status === "form";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setFormError(null);
    setStatus("submitting");
    try {
      const r = await authApi.acceptInvite(token, pw);
      if (r.ok && r.result?.email && r.result?.tenant_id) {
        setSession({
          email: r.result.email,
          tenantId: r.result.tenant_id,
          signedInAt: Date.now(),
        });
        setStatus("success");
        setTimeout(() => navigate("/overview", { replace: true }), 400);
        return;
      }
      setStatus("error");
      setError(mapError(r.error));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // The server refused the password, not the token. Keep everything.
        setFormError(serverError(err) || "That password doesn't meet the rules. Try another.");
        setStatus("form");
      } else if (err instanceof ApiError && err.status === 429) {
        setFormError("Too many attempts — try again in a few minutes.");
        setStatus("form");
      } else if (err instanceof ApiError) {
        setStatus("error");
        setError(mapError(serverError(err) || err.message));
      } else {
        setFormError("Network error accepting your invite. Try again.");
        setStatus("form");
      }
    }
  };

  return (
    <AuthLayout centered>
      {(status === "form" || status === "submitting") && (
        <>
          <p className="app-eyebrow">
            <span className="dot" /> You've been invited
          </p>
          <h1 className="app-h1">
            Choose your <em>password</em>.
          </h1>
          <p className="app-lede" style={{ margin: "0 auto 28px" }}>
            You'll use it with your email to sign in from now on.
          </p>
          <form onSubmit={onSubmit} noValidate style={{ textAlign: "left" }}>
            <div className="field">
              <label htmlFor="invite-password">Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="invite-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  aria-describedby="invite-password-rules"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  disabled={status === "submitting"}
                  placeholder="At least 8 characters"
                  style={{ width: "100%", paddingRight: "64px" }}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowPw((v) => !v)}
                  aria-pressed={showPw}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)" }}
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <p id="invite-password-rules" className="hint" aria-live="polite">
                <span style={{ color: lengthOk ? "var(--mint-text)" : "var(--ink-faint)" }}>8–128 characters</span>
                {" · "}
                <span style={{ color: compositionOk ? "var(--mint-text)" : "var(--ink-faint)" }}>a letter and a number</span>
              </p>
            </div>
            <div className="field">
              <label htmlFor="invite-password-confirm">Confirm password</label>
              <input
                id="invite-password-confirm"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                // Only while the mismatch line is on screen: a described-by
                // pointing at nothing is a dangling reference, not a hint.
                aria-describedby={confirm.length > 0 && !matches ? "invite-password-mismatch" : undefined}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={status === "submitting"}
                style={{ width: "100%" }}
              />
              {confirm.length > 0 && !matches && (
                <p id="invite-password-mismatch" className="err" style={{ color: "var(--rose)" }}>Passwords don't match.</p>
              )}
            </div>
            {formError && (
              <p className="err" role="alert" style={{ color: "var(--rose)" }}>
                {formError}
              </p>
            )}
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {status === "submitting" ? "Joining…" : "Join the team →"}
            </button>
          </form>
        </>
      )}
      {status === "success" && (
        <>
          <AuthStatusMark name="check" />
          <p className="app-eyebrow">
            <span className="dot" /> You're in
          </p>
          <h1 className="app-h1">
            Welcome to the <em>team</em>.
          </h1>
          <p className="app-lede" style={{ margin: "0 auto" }}>
            You're signed in. Next time, sign in with your email and the password you just chose.
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <AuthStatusMark name="alert" tone="warn" />
          <p className="app-eyebrow">
            <span className="dot" style={{ background: "var(--rose)" }} />{" "}
            Invite couldn't be accepted
          </p>
          <h1 className="app-h1">
            Something <em>didn't match</em>.
          </h1>
          <p className="app-lede" style={{ margin: "0 auto 28px" }}>
            {error}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/signin", { replace: true })}
          >
            Go to sign in →
          </button>
        </>
      )}
    </AuthLayout>
  );
}

/** The server's own `error` string off an ApiError body, if it carried one. */
function serverError(err: ApiError): string | null {
  const body = err.body;
  if (body && typeof body === "object" && "error" in body) {
    const value = (body as { error: unknown }).error;
    return typeof value === "string" && value ? value : null;
  }
  return null;
}

/** Maps backend error codes to sentence-case, no-exclamation copy. */
function mapError(code?: string | null): string {
  if (code === "invite_expired") {
    return "This invite link has expired — ask the account owner to send a new one.";
  }
  // invite_invalid, 404s, and anything else share the same generic copy
  // (no oracle distinguishing revoked/accepted/unknown tokens).
  return "This invite link isn't valid anymore.";
}
